import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VotingReminder = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUnsubscribed, setShowUnsubscribed] = useState(false);
  const fileInputRef = useRef(null);

  // New recipient form
  const [newRecipient, setNewRecipient] = useState({ fname: '', lname: '', email: '' });
  const [addingRecipient, setAddingRecipient] = useState(false);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [showUnsubscribed]);

  const fetchData = async () => {
    try {
      const [statsRes, logsRes, recipientsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/voting-reminder/stats`),
        axios.get(`${BACKEND_URL}/api/voting-reminder/logs?limit=20`),
        axios.get(`${BACKEND_URL}/api/voting-reminder/recipients?include_unsubscribed=${showUnsubscribed}`)
      ]);
      
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
      setRecipients(recipientsRes.data.recipients || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/voting-reminder/start`);
      fetchData();
      alert('Reminder scheduler started!');
    } catch (error) {
      alert('Failed to start scheduler');
    }
  };

  const handleStop = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/voting-reminder/stop`);
      fetchData();
      alert('Reminder scheduler stopped');
    } catch (error) {
      alert('Failed to stop scheduler');
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/voting-reminder/send-now`);
      fetchData();
      alert('Reminder sent successfully!');
    } catch (error) {
      alert('Failed to send reminder');
    } finally {
      setSending(false);
    }
  };

  const handleAddRecipient = async (e) => {
    e.preventDefault();
    if (!newRecipient.email) {
      alert('Email is required');
      return;
    }
    
    setAddingRecipient(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/voting-reminder/recipients`, newRecipient);
      alert(response.data.message);
      setNewRecipient({ fname: '', lname: '', email: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add recipient');
    } finally {
      setAddingRecipient(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/voting-reminder/recipients/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(`${response.data.message}\n${response.data.errors?.length ? 'Errors: ' + response.data.errors.join(', ') : ''}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUnsubscribe = async (recipientId) => {
    if (!window.confirm('Are you sure you want to unsubscribe this recipient?')) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/voting-reminder/recipients/${recipientId}/unsubscribe`);
      fetchData();
    } catch (error) {
      alert('Failed to unsubscribe');
    }
  };

  const handleDelete = async (recipientId) => {
    if (!window.confirm('Are you sure you want to permanently delete this recipient?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/voting-reminder/recipients/${recipientId}`);
      fetchData();
    } catch (error) {
      alert('Failed to delete');
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const getCampaignStatusBadge = (status) => {
    const styles = {
      'not_started': 'bg-yellow-100 text-yellow-700',
      'active': 'bg-green-100 text-green-700',
      'ended': 'bg-gray-100 text-gray-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🗳️ Voting Reminder System</h1>
          <p className="text-gray-600">Fort Worth Magazine - Best of 2026 Campaign</p>
        </div>
        <div className="flex gap-2">
          {stats?.is_active ? (
            <button onClick={handleStop} className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600">
              ⏹️ Stop Scheduler
            </button>
          ) : (
            <button onClick={handleStart} className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600">
              ▶️ Start Scheduler
            </button>
          )}
          <button onClick={handleSendNow} disabled={sending} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50">
            {sending ? '📤 Sending...' : '📧 Send Now'}
          </button>
        </div>
      </div>

      {/* Campaign Info Banner */}
      <div className="bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold mb-2">🏆 Top Dental Office - Fort Worth Magazine 2026</h2>
            <p className="opacity-90">Vote once every hour until January 31, 2026</p>
          </div>
          <a href={stats?.voting_url} target="_blank" rel="noopener noreferrer" className="bg-white text-teal-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition">
            🗳️ Vote Now
          </a>
        </div>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
            <p className="text-gray-600 text-sm">Reminders Sent</p>
            <p className="text-3xl font-bold text-green-600">{stats.total_sent}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
            <p className="text-gray-600 text-sm">Failed</p>
            <p className="text-3xl font-bold text-red-600">{stats.total_failed}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
            <p className="text-gray-600 text-sm">Scheduler</p>
            <p className="text-lg font-bold">
              {stats.is_active ? <span className="text-green-600">🟢 Active</span> : <span className="text-gray-500">⚪ Stopped</span>}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
            <p className="text-gray-600 text-sm">Campaign</p>
            <span className={`px-2 py-1 rounded text-sm ${getCampaignStatusBadge(stats.campaign_status)}`}>
              {stats.campaign_status === 'active' ? '🟢 Active' : stats.campaign_status === 'not_started' ? '🟡 Not Started' : '⚫ Ended'}
            </span>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
            <p className="text-gray-600 text-sm">Days Left</p>
            <p className="text-3xl font-bold text-orange-600">{stats.days_remaining}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-teal-500">
            <p className="text-gray-600 text-sm">Active Recipients</p>
            <p className="text-3xl font-bold text-teal-600">{stats.active_recipients}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-gray-500">
            <p className="text-gray-600 text-sm">Total Recipients</p>
            <p className="text-3xl font-bold text-gray-600">{stats.total_recipients}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {[
          { id: 'dashboard', label: '📊 Dashboard' },
          { id: 'recipients', label: `👥 Recipients (${stats?.active_recipients || 0})` },
          { id: 'logs', label: '📋 Logs' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium ${activeTab === tab.id ? 'text-teal-600 border-b-2 border-teal-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && stats && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold text-gray-800 mb-2">📤 Last Reminder Sent</h3>
              <p className="text-lg text-gray-600">{stats.last_sent ? formatDate(stats.last_sent) : 'No reminders sent yet'}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold text-gray-800 mb-2">⏰ Next Scheduled</h3>
              <p className="text-lg text-gray-600">
                {stats.is_active && stats.next_scheduled ? (
                  <>{formatDate(stats.next_scheduled)} <span className="text-sm text-teal-600 ml-2">({stats.hours_until_next.toFixed(1)} hours)</span></>
                ) : 'Scheduler not active'}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">📅 Voting Period</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-700">
              <div><strong>Start:</strong> Wednesday, December 17, 2025 at 8:00 AM CT</div>
              <div><strong>End:</strong> Saturday, January 31, 2026 at 11:59 PM CT</div>
            </div>
          </div>
        </div>
      )}

      {/* Recipients Tab */}
      {activeTab === 'recipients' && (
        <div className="space-y-6">
          {/* Add Single Recipient */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-800 mb-3">➕ Add Single Recipient</h3>
            <form onSubmit={handleAddRecipient} className="flex flex-wrap gap-3">
              <input
                type="text"
                placeholder="First Name"
                value={newRecipient.fname}
                onChange={(e) => setNewRecipient({ ...newRecipient, fname: e.target.value })}
                className="border rounded-lg px-3 py-2 w-40"
              />
              <input
                type="text"
                placeholder="Last Name"
                value={newRecipient.lname}
                onChange={(e) => setNewRecipient({ ...newRecipient, lname: e.target.value })}
                className="border rounded-lg px-3 py-2 w-40"
              />
              <input
                type="email"
                placeholder="Email Address *"
                value={newRecipient.email}
                onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                className="border rounded-lg px-3 py-2 flex-1 min-w-[200px]"
                required
              />
              <button
                type="submit"
                disabled={addingRecipient}
                className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {addingRecipient ? 'Adding...' : 'Add Recipient'}
              </button>
            </form>
          </div>

          {/* Upload CSV */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-800 mb-3">📤 Upload CSV/TXT File</h3>
            <p className="text-sm text-gray-600 mb-3">
              Upload a CSV file with columns: <code className="bg-gray-100 px-1 rounded">fname</code>, <code className="bg-gray-100 px-1 rounded">lname</code>, <code className="bg-gray-100 px-1 rounded">email</code>
            </p>
            <div className="flex items-center gap-3">
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="border rounded-lg px-3 py-2"
              />
              {uploading && <span className="text-teal-600">Uploading...</span>}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Example: fname,lname,email<br />
              John,Smith,john@example.com
            </p>
          </div>

          {/* Recipients List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">👥 Recipients List</h3>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showUnsubscribed}
                  onChange={(e) => setShowUnsubscribed(e.target.checked)}
                  className="rounded"
                />
                Show unsubscribed
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recipients.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">No recipients found</td>
                    </tr>
                  ) : (
                    recipients.map((r) => (
                      <tr key={r.id} className={`hover:bg-gray-50 ${!r.subscribed ? 'bg-gray-50 opacity-60' : ''}`}>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {r.fname || r.lname ? `${r.fname} ${r.lname}`.trim() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{r.email}</td>
                        <td className="px-4 py-3">
                          {r.subscribed ? (
                            <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">Active</span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">Unsubscribed</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(r.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {r.subscribed && (
                              <button
                                onClick={() => handleUnsubscribe(r.id)}
                                className="text-orange-600 hover:text-orange-700 text-sm"
                              >
                                Unsubscribe
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(r.id)}
                              className="text-red-600 hover:text-red-700 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">📋 Recent Reminder Logs</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent At</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">No reminders sent yet</td>
                  </tr>
                ) : (
                  logs.map((log, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDate(log.sent_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${log.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {log.status === 'sent' ? '✓ Sent' : '✗ Failed'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{log.recipient_count || log.recipients?.length || 0} recipients</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{log.error || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Official Rules */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
        <h3 className="font-semibold text-gray-800 mb-3">📋 Official Rules (Included in Emails)</h3>
        <div className="text-sm text-gray-600 space-y-2">
          <p><strong>Nomination Period:</strong> Dec 17, 2025 8:00 AM CT - Jan 31, 2026 11:59 PM CT</p>
          <p><strong>Frequency:</strong> Users may cast one (1) nomination in each business sub-category every hour.</p>
        </div>
      </div>
    </div>
  );
};

export default VotingReminder;
